import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { isAuthenticated, clearAuthData } from '../utils/tokenStorage';
import { subscriptionService } from '../services/subscriptionService';
import {
  AlertTriangle,
  ServerOff,
  WifiOff,
  Home,
  LogOut,
  RefreshCw,
  ShieldAlert
} from 'lucide-react';

const SubscriptionRenewal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null);

  const [errorState, setErrorState] = useState(() => {
    const urlError = searchParams.get('error');
    if (urlError) {
      return { type: urlError };
    }
    const storedError = sessionStorage.getItem('subscriptionCheckError');
    if (storedError) {
      try {
        return JSON.parse(storedError);
      } catch (e) {
        return null;
      }
    }
    return null;
  });

  useEffect(() => {
    return () => {
      sessionStorage.removeItem('subscriptionCheckError');
    };
  }, []);

  const getStatusConfig = () => {
    if (!errorState) {
      return {
        icon: ShieldAlert,
        title: t('subscription.renewal.title', 'Subscription Required'),
        message: t('subscription.renewal.message', 'Unable to access the application. Please contact your administrator to renew your subscription.'),
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        showRetry: true,
        showHome: true
      };
    }

    switch (errorState.type) {
      case 'server_error':
        return {
          icon: ServerOff,
          title: t('subscription.error.server.title', 'Server Error'),
          message: t('subscription.error.server.message', 'Our servers are experiencing issues. Please try again in a few moments.'),
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          showRetry: true,
          showHome: true
        };

      case 'network_error':
        return {
          icon: WifiOff,
          title: t('subscription.error.network.title', 'Connection Error'),
          message: t('subscription.error.network.message', 'Unable to connect to the server. Please check your internet connection.'),
          color: 'text-amber-600',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          showRetry: true,
          showHome: true
        };

      case 'auth_error':
        return {
          icon: AlertTriangle,
          title: t('subscription.error.auth.title', 'Session Expired'),
          message: t('subscription.error.auth.message', 'Your session has expired. Please log in again.'),
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          showRetry: false,
          showHome: false,
          showLogin: true
        };

      default:
        return {
          icon: AlertTriangle,
          title: t('subscription.renewal.title', 'Subscription Required'),
          message: t('subscription.renewal.message', 'Unable to access the application. Please contact your administrator.'),
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          showRetry: true,
          showHome: true
        };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  const handleRetry = async () => {
    setIsRetrying(true);
    setRetryResult(null);

    sessionStorage.removeItem('subscriptionCheckError');
    setErrorState(null);

    if (!isAuthenticated()) {
      clearAuthData();
      setIsRetrying(false);
      setRetryResult({ authExpired: true });
      return;
    }

    try {
      const timestamp = new Date().getTime();
      const response = await subscriptionService.getMyCompanySubscription({ _t: timestamp });

      if (response.success && response.data) {
        const subscription = response.data;
        const isActive = subscription.status === 'active';
        const currentDate = new Date();
        const endDate = subscription.endDate ? new Date(subscription.endDate) : null;
        const isNotExpired = !endDate || endDate > currentDate;

        if (isActive && isNotExpired) {
          window.dispatchEvent(new CustomEvent('subscription:refresh'));
          setIsRetrying(false);
          navigate('/app', { replace: true });
          return;
        }

        setIsRetrying(false);
        setRetryResult({ stillInactive: true, subscription });
        return;
      }

      setIsRetrying(false);
      setRetryResult({ stillInactive: true });
    } catch (error) {
      setIsRetrying(false);

      if (error.status === 401 || error.status === 403) {
        clearAuthData();
        setRetryResult({ authExpired: true });
        return;
      }

      if (error.status >= 500) {
        setRetryResult({ serverError: true });
        return;
      }

      setRetryResult({ networkError: true });
    }
  };

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  const handleGoToLogin = () => {
    clearAuthData();
    navigate('/login', { replace: true });
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      clearAuthData();
      navigate('/login', { replace: true });
    }
  };

  const getRetryMessage = () => {
    if (!retryResult) return null;

    if (retryResult.authExpired) {
      return t('subscription.retry.authExpired', 'Your session has expired. Please log in again.');
    }
    if (retryResult.stillInactive) {
      return t('subscription.retry.stillInactive', 'Your subscription is still inactive. Please contact your administrator.');
    }
    if (retryResult.serverError) {
      return t('subscription.retry.serverError', 'Server error. Please try again later.');
    }
    if (retryResult.networkError) {
      return t('subscription.retry.networkError', 'Network error. Please check your connection and try again.');
    }
    return null;
  };

  const retryMessage = getRetryMessage();

  const shouldShowLoginButton = statusConfig.showLogin || retryResult?.authExpired;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex flex-col">
      <header className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-lg">ER</span>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">
              EBIOS Risk Manager
            </h1>
          </div>
        </div>
      </header>

      <main className="flex-grow flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${statusConfig.bgColor} ${statusConfig.borderColor} border-2 mb-6`}>
              <StatusIcon className={`w-10 h-10 ${statusConfig.color}`} />
            </div>

            <h2 className={`text-2xl font-bold mb-4 ${statusConfig.color}`}>
              {statusConfig.title}
            </h2>

            <p className="text-gray-600 mb-8 leading-relaxed">
              {statusConfig.message}
            </p>

            {retryMessage && (
              <div className={`mb-6 p-4 rounded-lg text-sm ${
                retryResult?.authExpired
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : retryResult?.stillInactive
                  ? 'bg-orange-50 text-orange-700 border border-orange-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {retryMessage}
              </div>
            )}

            <div className="space-y-3">
              {statusConfig.showHome && !retryResult?.authExpired && (
                <button
                  onClick={handleGoHome}
                  className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all shadow-sm hover:shadow-md"
                >
                  <Home className="w-5 h-5 mr-2" />
                  {t('subscription.actions.returnHome', 'Return to Home')}
                </button>
              )}

              {statusConfig.showRetry && !retryResult?.authExpired && (
                <button
                  onClick={handleRetry}
                  disabled={isRetrying}
                  className="w-full flex items-center justify-center px-6 py-3 bg-white text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`w-5 h-5 mr-2 ${isRetrying ? 'animate-spin' : ''}`} />
                  {isRetrying
                    ? t('subscription.actions.retrying', 'Checking...')
                    : t('subscription.actions.retry', 'Try Again')
                  }
                </button>
              )}

              {shouldShowLoginButton && (
                <button
                  onClick={handleGoToLogin}
                  className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all shadow-sm hover:shadow-md"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  {t('subscription.actions.login', 'Log In Again')}
                </button>
              )}

              {user?.role === 'admin' && !retryResult?.authExpired && (
                <button
                  onClick={() => navigate('/admin/subscription')}
                  className="w-full flex items-center justify-center px-6 py-3 bg-white text-blue-600 border border-blue-600 rounded-lg font-medium hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all shadow-sm"
                >
                  <ShieldAlert className="w-5 h-5 mr-2" />
                  {t('subscription.actions.manage', 'Gérer l\'abonnement')}
                </button>
              )}

              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center px-6 py-3 text-gray-500 hover:text-gray-700 font-medium transition-colors"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('subscription.actions.logout', 'Log Out')}
              </button>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              {t('subscription.support.message', 'Need help? Contact your system administrator.')}
            </p>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 py-4 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} EBIOS Risk Manager. {t('common.allRightsReserved', 'All rights reserved.')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SubscriptionRenewal;
